/**
 * <cba-scroller> — a row that scrolls sideways, with optional arrows and a
 * progress bar (plan 6.1: replaces the inline scripts of explore collections,
 * you may also like, the worn row and the collection pills).
 *
 *   <cba-scroller [center-current]>
 *     <div data-scroller-nav><button data-scroller-prev>…</button> <button data-scroller-next>…</button></div>
 *     <div data-scroller-track>…items…</div>
 *     <div><div data-scroller-progress></div></div>
 *   </cba-scroller>
 *
 * - The arrows move one item (its width plus the track's column gap) and are
 *   disabled at either end. [data-scroller-nav], the arrows' wrapper, is
 *   hidden when every item already shows, so there is nothing to scroll.
 * - The progress bar's width follows the scroll position.
 * - center-current scrolls the [aria-current="page"] item into the middle on
 *   load, without moving the page up or down.
 * - Listeners are dropped when the element leaves the page (the theme editor
 *   re-renders sections), so nothing is bound twice.
 */
// A section can load this file more than once on a page; only the first defines it.
if (!customElements.get('cba-scroller')) {
  class CbaScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroller-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      this.nav = this.querySelector('[data-scroller-nav]');
      this.progress = this.querySelector('[data-scroller-progress]');
      this.abort = new AbortController();
      const { signal } = this.abort;

      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this.update(); });
      }, { passive: true, signal });
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1), { signal });
      if (this.next) this.next.addEventListener('click', () => this.step(1), { signal });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);

      if (this.hasAttribute('center-current')) {
        const current = this.track.querySelector('[aria-current="page"]');
        if (current) current.scrollIntoView({ block: 'nearest', inline: 'center' });
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.resize) this.resize.disconnect();
    }

    step(direction) {
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      // Smooth unless the visitor asked for less motion (05 Motion).
      const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * distance, behavior: still ? 'auto' : 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = left <= 1;
      if (this.next) this.next.disabled = left >= max - 1;
      if (this.nav) this.nav.hidden = max <= 1;
      if (this.progress) this.progress.style.width = `${max > 0 ? (left / max) * 100 : 0}%`;
    }
  }

  customElements.define('cba-scroller', CbaScroller);
}
